import { calculateQuote } from "../lib/checkout";
import { mockBranches } from "../mocks/branches";
import { mockCategories } from "../mocks/categories";
import { mockOrders } from "../mocks/orders";
import { mockProducts } from "../mocks/products";
import { mockStores } from "../mocks/stores";
import type {
  CheckoutQuote,
  OnlineOrder,
  PlaceOrderPayload,
  ProductFilters,
  ProductResult,
  StorefrontBranch,
  StorefrontCategory,
  StorefrontConfig,
  StoreDirectoryEntry,
  StorefrontProduct
} from "../types/storefront";
import { apiClient } from "./apiClient";
import { storefrontDevLog } from "../lib/devLog";

const useMocks = import.meta.env.VITE_USE_MOCK_STOREFRONT_API === "true";
const wait = (ms = 220) => new Promise((resolve) => setTimeout(resolve, ms));
const resolveAssetUrl = (value?: string | null) => {
  if (!value || /^https?:\/\//i.test(value) || value.startsWith("data:")) return value || undefined;
  const apiBase = String(apiClient.defaults.baseURL || window.location.origin);
  try {
    return new URL(value, new URL(apiBase, window.location.origin).origin).toString();
  } catch {
    return value;
  }
};
type Envelope<T> = { success: boolean; message: string; data: T };
const unwrap = <T>(response: { data: Envelope<T> | T }): T => {
  const payload = response.data;
  return typeof payload === "object" && payload !== null && "success" in payload
    ? (payload as Envelope<T>).data
    : payload as T;
};

type PublicProduct = {
  id: string; name: string; slug: string; description?: string; images: string[];
  category?: { id: string; name: string; slug?: string } | null; unit?: string;
  websitePrice: number; stockStatus: StorefrontProduct["stockStatus"]; availableQuantity?: number | null;
};

type TrackedOrderResponse = {
  orderReference: string;
  status: OnlineOrder["status"];
  orderType: OnlineOrder["orderType"];
  paymentMethod: "cod";
  paymentStatus: "unpaid" | "paid";
  items: Array<{ name: string; quantity: number; unitPrice: number; lineTotal: number }>;
  subtotal: number;
  deliveryCharge: number;
  grandTotal: number;
  createdAt: string;
  updatedAt?: string;
  deliveryAddress?: PlaceOrderPayload["deliveryAddress"];
  statusHistory?: OnlineOrder["statusHistory"];
};

const normalizeProduct = (storeSlug: string, branchId: string, product: PublicProduct): StorefrontProduct => ({
  id: product.id,
  tenantId: storeSlug,
  tenantSlug: storeSlug,
  categoryId: product.category?.id || "",
  branchIds: [branchId],
  name: product.name,
  slug: product.slug,
  shortDescription: product.description,
  description: product.description,
  price: product.websitePrice,
  websitePrice: product.websitePrice,
  images: (product.images || []).map(resolveAssetUrl).filter(Boolean) as string[],
  thumbnail: resolveAssetUrl(product.images?.[0]),
  unit: product.unit,
  showOnWebsite: true,
  isActive: true,
  isArchived: false,
  websiteStatus: "published",
  isFeatured: false,
  availableForDelivery: true,
  availableForPickup: true,
  stockStatus: product.stockStatus,
  availableQuantity: product.availableQuantity ?? undefined,
  createdAt: new Date(0).toISOString()
});

const isPublished = (product: StorefrontProduct) =>
  product.showOnWebsite &&
  product.isActive &&
  !product.isArchived &&
  product.websiteStatus === "published";

const normalizeTrackedOrder = (
  storeSlug: string,
  phone: string,
  tracked: TrackedOrderResponse
): OnlineOrder => ({
  orderId: tracked.orderReference,
  orderReference: tracked.orderReference,
  tenantSlug: storeSlug,
  branchId: "",
  status: tracked.status,
  paymentMethod: tracked.paymentMethod,
  paymentStatus: tracked.paymentStatus,
  subtotal: tracked.subtotal,
  deliveryCharge: tracked.deliveryCharge,
  discount: 0,
  grandTotal: tracked.grandTotal,
  customer: { name: "Customer", phone },
  deliveryAddress: tracked.deliveryAddress,
  orderType: tracked.orderType,
  items: tracked.items.map((item, index) => ({
    product: normalizeProduct(storeSlug, "", {
      id: `${tracked.orderReference}-${index}`,
      name: item.name,
      slug: `${tracked.orderReference}-${index}`,
      images: [],
      websitePrice: item.unitPrice,
      stockStatus: "in_stock"
    }),
    quantity: item.quantity
  })),
  createdAt: tracked.createdAt,
  updatedAt: tracked.updatedAt,
  statusHistory: tracked.statusHistory
});

export const storefrontApi = {
  async getPublicStores(): Promise<StoreDirectoryEntry[]> {
    if (!useMocks) {
      const data = unwrap(await apiClient.get<Envelope<StoreDirectoryEntry[]>>("/stores"));
      return data.map((store) => ({ ...store, logo: resolveAssetUrl(store.logo) }));
    }
    await wait();
    return mockStores
      .filter((store) => store.storefrontEnabled)
      .map((store) => ({
        storeSlug: store.storeSlug,
        storeName: store.storeName,
        logo: resolveAssetUrl(store.logo),
        phone: store.phone,
        address: store.address,
        themeId: store.themeId,
        deliveryEnabled: store.deliveryEnabled,
        pickupEnabled: store.pickupEnabled
      }));
  },

  async getStorefrontConfig(storeSlug: string): Promise<StorefrontConfig | null> {
    if (!useMocks) {
      const data = unwrap(await apiClient.get<Envelope<StorefrontConfig>>(`/stores/${storeSlug}`));
      return {
        ...data,
        tagline: data.tagline || "Shop online from your trusted local store.",
        heroTitle: data.heroTitle || data.storeName,
        heroSubtitle: data.heroSubtitle || "Browse live branch stock and order for delivery or pickup.",
        primaryColor: data.primaryColor || "#0b659b",
        secondaryColor: data.secondaryColor || "#071a32",
        businessHours: data.businessHours || [],
        deliveryCharge: (data as StorefrontConfig & { flatDeliveryCharge?: number }).flatDeliveryCharge ?? data.deliveryCharge ?? 0,
        freeDeliveryThreshold: (data as StorefrontConfig & { freeDeliveryAbove?: number }).freeDeliveryAbove ?? data.freeDeliveryThreshold,
        storefrontEnabled: true,
        ordersWhileClosed: true
      };
    }
    await wait();
    return mockStores.find((store) => store.storeSlug === storeSlug) ?? null;
  },

  async getStorefrontBranches(storeSlug: string): Promise<StorefrontBranch[]> {
    if (!useMocks) {
      const data = unwrap(await apiClient.get<Envelope<StorefrontBranch[]>>(`/stores/${storeSlug}/branches`));
      return data.map((branch) => ({
        ...branch,
        tenantSlug: storeSlug,
        isActive: true,
        address: branch.address || "",
        phone: branch.phone || "",
        estimatedDeliveryTime: branch.estimatedDeliveryTime || "35-50 minutes",
        businessHours: branch.businessHours || []
      }));
    }
    await wait(100);
    return mockBranches.filter((branch) => branch.tenantSlug === storeSlug && branch.isActive);
  },

  async getStorefrontCategories(storeSlug: string, branchId?: string): Promise<StorefrontCategory[]> {
    if (!useMocks) {
      const data = unwrap(await apiClient.get<Envelope<StorefrontCategory[]>>(`/stores/${storeSlug}/categories`, {
        params: { branchId }
      }));
      storefrontDevLog("Categories API response", { storeSlug, branchId, count: data.length });
      return data.map((category) => ({
        ...category,
        tenantSlug: storeSlug,
        slug: category.slug || category.id,
        sortOrder: category.sortOrder || 0
      }));
    }
    await wait(100);
    const availableCategoryIds = new Set(
      mockProducts
        .filter((product) => product.tenantSlug === storeSlug && (!branchId || product.branchIds.includes(branchId)))
        .map((product) => product.categoryId)
    );
    return mockCategories.filter(
      (category) => category.tenantSlug === storeSlug && availableCategoryIds.has(category.id)
    );
  },

  async getStorefrontProducts(
    storeSlug: string,
    branchId: string,
    filters: ProductFilters = {}
  ): Promise<ProductResult> {
    if (!useMocks) {
      const page = Math.floor((filters.offset || 0) / (filters.limit || 24)) + 1;
      const sort = {
        "price-asc": "price_asc",
        "price-desc": "price_desc",
        name: "name_asc",
        newest: "newest",
        featured: "newest"
      }[filters.sort || "featured"];
      const data = unwrap(await apiClient.get<Envelope<{
        items: PublicProduct[];
        pagination: { total: number };
      }>>(`/stores/${storeSlug}/products`, {
        params: {
          branchId,
          category: filters.categorySlug,
          search: filters.query,
          inStock: filters.availability?.length === 1 && filters.availability[0] === "in_stock" ? true : undefined,
          page,
          limit: filters.limit || 24,
          sort
        }
      }));
      storefrontDevLog("Products API response", {
        storeSlug,
        branchId,
        filters,
        returned: data.items.length,
        total: data.pagination.total
      });
      return {
        items: data.items.map((product) => normalizeProduct(storeSlug, branchId, product)),
        total: data.pagination.total
      };
    }
    await wait();
    const categories = mockCategories.filter((category) => category.tenantSlug === storeSlug);
    const category = categories.find((item) => item.slug === filters.categorySlug);
    let items = mockProducts.filter(
      (product) =>
        product.tenantSlug === storeSlug &&
        product.branchIds.includes(branchId) &&
        isPublished(product) &&
        (!category || product.categoryId === category.id)
    );
    if (filters.query) {
      const query = filters.query.toLowerCase();
      items = items.filter((product) =>
        [product.websiteName, product.name, product.brand, product.shortDescription]
          .filter(Boolean)
          .some((value) => value?.toLowerCase().includes(query))
      );
    }
    if (filters.brands?.length) items = items.filter((product) => filters.brands?.includes(product.brand ?? ""));
    if (filters.availability?.length) {
      items = items.filter((product) => filters.availability?.includes(product.stockStatus));
    }
    if (filters.minPrice != null) items = items.filter((product) => (product.websitePrice ?? product.price) >= filters.minPrice!);
    if (filters.maxPrice != null) items = items.filter((product) => (product.websitePrice ?? product.price) <= filters.maxPrice!);
    const sort = filters.sort ?? "featured";
    items.sort((a, b) => {
      if (sort === "price-asc") return (a.websitePrice ?? a.price) - (b.websitePrice ?? b.price);
      if (sort === "price-desc") return (b.websitePrice ?? b.price) - (a.websitePrice ?? a.price);
      if (sort === "name") return (a.websiteName ?? a.name).localeCompare(b.websiteName ?? b.name);
      if (sort === "newest") return b.createdAt.localeCompare(a.createdAt);
      return Number(b.isFeatured) - Number(a.isFeatured);
    });
    const total = items.length;
    const offset = filters.offset ?? 0;
    return { items: items.slice(offset, offset + (filters.limit ?? 100)), total };
  },

  async getStorefrontProduct(storeSlug: string, productSlug: string, branchId: string) {
    if (!useMocks) {
      const data = unwrap(await apiClient.get<Envelope<{ product: PublicProduct }>>(
        `/stores/${storeSlug}/products/${productSlug}`,
        { params: { branchId } }
      ));
      return normalizeProduct(storeSlug, branchId, data.product);
    }
    const result = await this.getStorefrontProducts(storeSlug, branchId);
    return result.items.find((product) => product.slug === productSlug) ?? null;
  },

  async calculateCheckout(storeSlug: string, payload: PlaceOrderPayload): Promise<CheckoutQuote> {
    if (!useMocks) {
      const data = unwrap(await apiClient.post<Envelope<CheckoutQuote>>(
        `/stores/${storeSlug}/checkout/calculate`,
        {
          branchId: payload.branchId,
          orderType: payload.orderType,
          items: payload.items
        }
      ));
      storefrontDevLog("Checkout totals", {
        storeSlug,
        branchId: payload.branchId,
        subtotal: data.subtotal,
        deliveryCharge: data.deliveryCharge,
        grandTotal: data.grandTotal
      });
      return { ...data, freeDeliveryRemaining: 0 };
    }
    const store = await this.getStorefrontConfig(storeSlug);
    if (!store) throw new Error("Store is unavailable.");
    const products = await this.getStorefrontProducts(storeSlug, payload.branchId);
    const items = payload.items.map((item) => {
      const product = products.items.find((candidate) => candidate.id === item.productId);
      if (!product || product.stockStatus === "out_of_stock") throw new Error("A cart item is no longer available.");
      if (item.quantity > (product.availableQuantity ?? Infinity)) throw new Error(`${product.name} has insufficient stock.`);
      return { product, quantity: item.quantity };
    });
    return calculateQuote(items, store, payload.orderType);
  },

  async placeOnlineOrder(storeSlug: string, payload: PlaceOrderPayload): Promise<OnlineOrder> {
    if (!useMocks) {
      const idempotencyKey = payload.idempotencyKey || crypto.randomUUID();
      const [quote, products, orderData] = await Promise.all([
        this.calculateCheckout(storeSlug, payload),
        this.getStorefrontProducts(storeSlug, payload.branchId, { limit: 100 }),
        apiClient.post<Envelope<{
          orderReference: string; status: OnlineOrder["status"]; grandTotal: number;
          orderType: OnlineOrder["orderType"]; createdAt: string;
        }>>(`/stores/${storeSlug}/orders`, { ...payload, idempotencyKey })
      ]);
      const placed = unwrap(orderData);
      const items = payload.items.map((item) => ({
        product: products.items.find((product) => product.id === item.productId)!,
        quantity: item.quantity
      })).filter((item) => item.product);
      const order: OnlineOrder = {
        orderId: placed.orderReference,
        orderReference: placed.orderReference,
        tenantSlug: storeSlug,
        branchId: payload.branchId,
        status: placed.status,
        paymentMethod: "cod",
        paymentStatus: "unpaid",
        ...quote,
        customer: payload.customer,
        deliveryAddress: payload.deliveryAddress,
        orderType: payload.orderType,
        items,
        estimatedDeliveryTime: "35-50 minutes",
        createdAt: placed.createdAt
      };
      sessionStorage.setItem(`elmor-order:${order.orderReference}`, JSON.stringify(order));
      return order;
    }
    const quote = await this.calculateCheckout(storeSlug, payload);
    await wait(500);
    const allProducts = await this.getStorefrontProducts(storeSlug, payload.branchId);
    const items = payload.items.map((item) => ({
      product: allProducts.items.find((product) => product.id === item.productId)!,
      quantity: item.quantity
    }));
    const orderReference = `ELM-${Date.now().toString().slice(-8)}`;
    const order: OnlineOrder = {
      orderId: crypto.randomUUID(),
      orderReference,
      tenantSlug: storeSlug,
      branchId: payload.branchId,
      status: "pending",
      paymentMethod: "cod",
      paymentStatus: "unpaid",
      ...quote,
      customer: payload.customer,
      deliveryAddress: payload.deliveryAddress,
      orderType: payload.orderType,
      items,
      estimatedDeliveryTime: "35-50 minutes",
      createdAt: new Date().toISOString()
    };
    mockOrders.set(orderReference, order);
    sessionStorage.setItem(`elmor-order:${orderReference}`, JSON.stringify(order));
    return order;
  },

  async getOrderByReference(storeSlug: string, orderReference: string, phone?: string): Promise<OnlineOrder | null> {
    if (!useMocks) {
      const saved = sessionStorage.getItem(`elmor-order:${orderReference}`);
      if (!phone && saved) return JSON.parse(saved) as OnlineOrder;
      if (!phone) return null;
      return this.trackOrder(storeSlug, { orderReference, phone });
    }
    await wait();
    const saved = sessionStorage.getItem(`elmor-order:${orderReference}`);
    const order = mockOrders.get(orderReference) ?? (saved ? (JSON.parse(saved) as OnlineOrder) : null);
    if (!order || order.tenantSlug !== storeSlug) return null;
    if (phone && !order.customer.phone.endsWith(phone.replace(/\D/g, "").slice(-10))) return null;
    return order;
  },

  async trackOrder(
    storeSlug: string,
    payload: { orderReference: string; phone: string },
    signal?: AbortSignal
  ): Promise<OnlineOrder | null> {
    if (!useMocks) {
      try {
        const tracked = unwrap(await apiClient.post<Envelope<TrackedOrderResponse>>(
          `/stores/${storeSlug}/orders/track`,
          payload,
          {
            signal,
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache"
            }
          }
        ));
        storefrontDevLog("Tracking API response", {
          storeSlug,
          orderReference: tracked.orderReference,
          status: tracked.status,
          updatedAt: tracked.updatedAt
        });
        return normalizeTrackedOrder(storeSlug, payload.phone, tracked);
      } catch (error) {
        if (error instanceof Error && /not found/i.test(error.message)) return null;
        throw error;
      }
    }
    return this.getOrderByReference(storeSlug, payload.orderReference, payload.phone);
  }
};
